import { useRef, useState } from "react";
import { AnimatePresence, motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight, Droplets, Layers, Palette } from "lucide-react";
import { CATEGORIES, PRODUCTS, getProduct } from "@/data/products";
import { MaskLines, Reveal, EASE } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { ProductCard } from "@/components/ProductCard";
import { CompareSlider } from "@/components/CompareSlider";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.08, 1.22]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 120, damping: 20 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 120, damping: 20 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section ref={ref} onMouseMove={onMove} className="relative overflow-hidden pt-24 md:pt-28" data-testid="hero-section">
      <div className="mx-auto grid max-w-[1440px] items-end gap-10 px-5 pb-16 md:px-10 lg:grid-cols-[1.15fr_1fr] lg:pb-24">
        <div>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1, duration: 1 }} className="mb-8 flex items-center gap-3 text-[11px] uppercase tracking-[0.28em] text-muted">
            <span className="h-px w-10 bg-ink/40" /> Borcelle · Collection 2027
          </motion.p>
          <h1 className="font-display text-[clamp(3.4rem,9.5vw,9.5rem)] leading-[0.88] tracking-[-0.02em]">
            <MaskLines lines={["Furniture", <em key="e" className="text-clay">that holds</em>, "a whole life."]} />
          </h1>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 1, ease: EASE }} className="mt-10 flex flex-col gap-8 sm:flex-row sm:items-end">
            <p className="max-w-sm text-base leading-relaxed text-muted">
              Architectural pieces in warm wood and quiet tones — designed to be lived with, not just looked at. Every piece in two signature finishes.
            </p>
            <a href="#collection" data-testid="hero-shop-button" className="btn-primary shrink-0">
              Explore the collection <ArrowDownRight className="h-4 w-4" />
            </a>
          </motion.div>
        </div>
        <motion.div
          style={{ rotateX: rx, rotateY: ry, transformPerspective: 1200 }}
          initial={{ clipPath: "inset(100% 0% 0% 0% round 999px 999px 4px 4px)" }}
          animate={{ clipPath: "inset(0% 0% 0% 0% round 999px 999px 4px 4px)" }}
          transition={{ duration: 1.5, ease: EASE, delay: 0.3 }}
          className="relative mx-auto aspect-[3/4] w-full max-w-[520px] overflow-hidden bg-sand"
        >
          <motion.img style={{ y, scale }} src={getProduct("spine").finishes[0].image} alt="Spine book rack in a sunlit living room" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-x-0 bottom-0 flex items-end justify-between bg-gradient-to-t from-black/50 to-transparent p-6 pt-20 text-white">
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] opacity-80">Featured</p>
              <p className="font-display text-3xl">Spine Book Rack</p>
            </div>
            <span className="spin-slow grid h-20 w-20 place-items-center">
              <svg viewBox="0 0 100 100" className="h-full w-full"><defs><path id="c" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" /></defs><text fill="white" fontSize="11" letterSpacing="3"><textPath href="#c">NEW · COLLECTION · 2027 · </textPath></text></svg>
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const Collection = () => {
  const [cat, setCat] = useState("all");
  const list = cat === "all" ? PRODUCTS : PRODUCTS.filter((p) => p.category === cat);
  return (
    <section id="collection" className="mx-auto max-w-[1440px] px-5 py-24 md:px-10 md:py-36" data-testid="collection-section">
      <Reveal className="flex flex-col justify-between gap-8 md:flex-row md:items-end">
        <h2 className="font-display text-5xl leading-[0.9] md:text-8xl">The <em className="text-clay">Collection</em></h2>
        <p className="max-w-xs text-sm text-muted">Choose a type of piece. Tap the swatches to see each one in both finishes.</p>
      </Reveal>
      <div className="no-scrollbar -mx-5 mt-12 flex gap-2 overflow-x-auto px-5 md:mx-0 md:flex-wrap md:px-0" data-testid="category-filter">
        {CATEGORIES.map((c) => {
          const n = c.id === "all" ? PRODUCTS.length : PRODUCTS.filter((p) => p.category === c.id).length;
          const active = c.id === cat;
          return (
            <button
              key={c.id}
              data-testid={`category-${c.id}`}
              onClick={() => setCat(c.id)}
              className={`relative shrink-0 rounded-full border px-5 py-2.5 text-[12px] uppercase tracking-[0.14em] transition-colors duration-300 ${active ? "border-ink text-bone" : "border-ink/20 hover:border-ink"}`}
            >
              {active && <motion.span layoutId="cat-pill" className="absolute inset-0 rounded-full bg-ink" transition={{ duration: 0.5, ease: EASE }} />}
              <span className="relative">{c.label} <sup className="opacity-60">{n}</sup></span>
            </button>
          );
        })}
      </div>
      <motion.div layout className="mt-14 grid gap-x-6 gap-y-16 sm:grid-cols-2 lg:grid-cols-3" data-testid="product-grid">
        <AnimatePresence>
          {list.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
        </AnimatePresence>
      </motion.div>
    </section>
  );
};

const Finishes = () => (
  <section id="finishes" className="bg-sand" data-testid="finishes-section">
    <div className="mx-auto grid max-w-[1440px] items-center gap-12 px-5 py-24 md:px-10 md:py-36 lg:grid-cols-[1fr_1.3fr]">
      <Reveal>
        <p className="text-[11px] uppercase tracking-[0.28em] text-muted">Two finishes, one language</p>
        <h2 className="mt-6 font-display text-5xl leading-[0.92] md:text-7xl">Light by day.<br /><em className="text-clay">Dark by nature.</em></h2>
        <p className="mt-8 max-w-md text-muted">Drag across the image to see the Nesta TV Stand move between our two signature finishes.</p>
        <div className="mt-10 space-y-5">
          {getProduct("nesta").finishes.map((f) => (
            <div key={f.id} className="flex items-center gap-4 border-t border-ink/15 pt-5">
              <span className="h-10 w-10 rounded-full" style={{ background: `linear-gradient(135deg, ${f.swatch[0]} 50%, ${f.swatch[1]} 50%)` }} />
              <div>
                <p className="font-display text-2xl leading-none">{f.name}</p>
                <p className="mt-1 text-xs uppercase tracking-[0.16em] text-muted">{f.id} finish</p>
              </div>
            </div>
          ))}
        </div>
      </Reveal>
      <Reveal delay={0.15}><CompareSlider product={getProduct("nesta")} /></Reveal>
    </div>
  </section>
);

const CRAFT = [
  { icon: Droplets, title: "Moisture Resistant", text: "Boards engineered to stay true through Indian monsoons and everyday spills." },
  { icon: Layers, title: "18mm Thicker Board", text: "A heavier, more solid build than standard flat-pack — it feels like real furniture." },
  { icon: Palette, title: "Two Signature Finishes", text: "Sheer & Nordic Walnut or Dark Grey & Mountain Oak — every piece, both ways." },
];

const Craft = () => (
  <section id="craft" className="mx-auto max-w-[1440px] px-5 py-24 md:px-10 md:py-36" data-testid="craft-section">
    <div className="grid gap-4 md:grid-cols-3 md:grid-rows-[auto_auto]">
      <Reveal className="relative overflow-hidden rounded-sm md:col-span-2 md:row-span-2">
        <img src={getProduct("alandra").finishes[1].image} alt="Alandra coffee table in dark finish" className="h-full max-h-[640px] w-full object-cover" loading="lazy" />
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/60 to-transparent" />
        <p className="absolute bottom-6 left-6 max-w-sm font-display text-4xl leading-none text-white drop-shadow md:text-5xl">Made to be <em>lived on.</em></p>
      </Reveal>
      {CRAFT.slice(0, 2).map((c, i) => (
        <Reveal key={c.title} delay={0.1 * (i + 1)} className="flex flex-col justify-between rounded-sm border border-ink/10 p-8">
          <c.icon className="h-7 w-7 text-clay" strokeWidth={1.3} />
          <div className="mt-10">
            <p className="font-display text-3xl">{c.title}</p>
            <p className="mt-3 text-sm leading-relaxed text-muted">{c.text}</p>
          </div>
        </Reveal>
      ))}
      <Reveal delay={0.2} className="flex flex-col justify-between gap-6 rounded-sm bg-ink p-8 text-bone md:col-span-3 md:flex-row md:items-center">
        <div className="flex items-center gap-5">
          <Palette className="h-7 w-7 text-clay" strokeWidth={1.3} />
          <p className="font-display text-3xl">{CRAFT[2].title}</p>
        </div>
        <p className="max-w-md text-sm text-bone/70">{CRAFT[2].text}</p>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <main>
      <Hero />
      <Marquee />
      <Collection />
      <Finishes />
      <Craft />
    </main>
  );
}
